import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Key, Trash2, Mail, Smartphone, Copy, Check } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { titleCase } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { AccessToggleButton } from "@/components/AccessToggleButton";

const ROLES = [
    { value: "driver", label: "Driver" },
    { value: "driver_helper", label: "Driver Helper" },
    { value: "dispatcher", label: "Dispatcher" },
    { value: "csr", label: "Customer Service Rep" },
    { value: "admin", label: "Admin (owner)" },
];

const ROLE_TONES = {
    admin: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    dispatcher: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    csr: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    driver: "bg-primary/10 text-primary border-primary/20",
    driver_helper: "bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20",
};

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const iAmOwner = users.some((x) => x.id === me?.id && x.is_owner);
    const [notify, setNotify] = useState(false);
    const [loading, setLoading] = useState(true);
    const [openNew, setOpenNew] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load users");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const resetPassword = async (u) => {
        const p = window.prompt(`New password for ${u.email}?\n(Give them this in person or via Signal — never plain email.)`);
        if (!p || p.length < 8) {
            if (p) toast.error("Password must be at least 8 characters");
            return;
        }
        try {
            await api.patch(`/users/${u.id}`, { password: p, notify });
            toast.success(`Password reset for ${u.email}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}? This can't be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const setRole = async (u, role) => {
        try {
            await api.patch(`/users/${u.id}`, { role, notify });
            toast.success(`Role updated to ${titleCase(role)}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <PageHeader
                title="Team & drivers"
                subtitle="Everyone who can log into HaulerOS — drivers, dispatchers, admins."
                actions={
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-xs text-muted-foreground mr-2 cursor-pointer"
                               title="When on, people get an email if you change their role or reset their password">
                            <Switch checked={notify} onCheckedChange={setNotify} data-testid="users-notify-switch" />
                            Email people about role/password changes
                        </label>
                        <DriverInstallGuideButton />
                        <NewUserSheet open={openNew} setOpen={setOpenNew}
                                      onCreated={() => { setOpenNew(false); load(); }} />
                    </div>
                }
            />

            <Card>
                <div className="overflow-x-auto" data-testid="users-table">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="border-b border-border surface">
                            <th className="text-left px-4 py-3 overline">Name</th>
                            <th className="text-left px-4 py-3 overline">Email</th>
                            <th className="text-left px-4 py-3 overline">Role</th>
                            <th className="text-left px-4 py-3 overline">Status</th>
                            <th className="text-right px-4 py-3 overline">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading && (
                            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No users yet. Click "Add user" to create one.</td></tr>
                        )}
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-border hover:bg-secondary/50"
                                data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                                <td className="px-4 py-3">
                                    <a href={`mailto:${u.email}`} className="hover:underline text-muted-foreground">{u.email}</a>
                                </td>
                                <td className="px-4 py-3">
                                    <Select value={u.role} onValueChange={(v) => setRole(u, v)}
                                            disabled={u.is_owner || (u.role === "admin" && u.id !== me?.id && !iAmOwner)}>
                                        <SelectTrigger className="h-7 w-40 text-xs" data-testid={`user-role-${u.id}`}>
                                            <Badge variant="outline" className={`text-[9px] tracking-wider ${ROLE_TONES[u.role] || ""}`}>
                                                {(u.role || "").toUpperCase()}
                                            </Badge>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ROLES.map((r) => (
                                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3 text-xs text-muted-foreground" data-testid={`user-status-${u.id}`}>
                                    {u.access_suspended ? (
                                        <Badge variant="outline" className="text-[9px] tracking-wider border-amber-400 text-amber-700 dark:text-amber-400">
                                            SUSPENDED
                                        </Badge>
                                    ) : (u.status || "active")}
                                </td>
                                <td className="px-4 py-3 text-right space-x-1">
                                    {u.id !== me?.id && !u.is_owner && (u.role !== "admin" || iAmOwner) && (
                                        <AccessToggleButton kind="user" id={u.id} name={u.email} compact
                                                            suspended={!!u.access_suspended} onChanged={load} />
                                    )}
                                    {!(u.role === "admin" && u.id !== me?.id && !iAmOwner) && (
                                        <Button size="sm" variant="ghost" onClick={() => resetPassword(u)}
                                                data-testid={`user-reset-${u.id}`}
                                                title="Reset password">
                                            <Key className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                    {u.id !== me?.id && !u.is_owner && (u.role !== "admin" || iAmOwner) && (
                                        <Button size="sm" variant="ghost" onClick={() => remove(u)}
                                                data-testid={`user-delete-${u.id}`}
                                                title="Delete user">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

function NewUserSheet({ open, setOpen, onCreated }) {
    const [form, setForm] = useState({
        name: "", email: "", password: "", role: "driver",
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name || !form.email || !form.password) {
            toast.error("Name, email, and password are all required");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setBusy(true);
        try {
            await api.post("/users", form);
            toast.success(`${titleCase(form.role)} account created for ${form.email}`);
            setForm({ name: "", email: "", password: "", role: "driver" });
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Create failed");
        } finally { setBusy(false); }
    };

    const genPassword = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
        let p = "";
        for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
        set("password", p);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button size="sm" data-testid="new-user-btn">
                    <UserPlus className="w-4 h-4 mr-1" />Add user
                </Button>
            </SheetTrigger>
            <SheetContent className="sm:max-w-md">
                <SheetHeader>
                    <SheetTitle>Add a new team member</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                    <Field label="Full name">
                        <Input value={form.name}
                               onChange={(e) => set("name", e.target.value)}
                               placeholder="Jane Driver"
                               data-testid="new-user-name" />
                    </Field>
                    <Field label="Email">
                        <Input type="email" value={form.email}
                               onChange={(e) => set("email", e.target.value)}
                               placeholder="jane@company.com"
                               data-testid="new-user-email" />
                    </Field>
                    <Field label="Role">
                        <Select value={form.role} onValueChange={(v) => set("role", v)}>
                            <SelectTrigger data-testid="new-user-role">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROLES.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Initial password">
                        <div className="flex gap-2">
                            <Input value={form.password}
                                   onChange={(e) => set("password", e.target.value)}
                                   placeholder="8+ characters"
                                   data-testid="new-user-password" />
                            <Button variant="outline" size="sm" onClick={genPassword} type="button"
                                    data-testid="new-user-gen-password">
                                Generate
                            </Button>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            Share via Signal or in person &mdash; not email.
                            They can change it after logging in.
                        </div>
                    </Field>
                    <div className="pt-2 flex items-start gap-2 text-xs bg-secondary/40 rounded p-3">
                        <Mail className="w-4 h-4 shrink-0 text-muted-foreground mt-0.5" />
                        <div className="text-muted-foreground">
                            Drivers log in at <code>/driver</code> after installing the PWA.
                            Dispatchers and admins log in at <code>/login</code>.
                        </div>
                    </div>
                    <Button className="w-full" onClick={submit} disabled={busy}
                            data-testid="new-user-submit">
                        {busy ? "Creating…" : "Create account"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">{label}</Label>
            {children}
        </div>
    );
}


function DriverInstallGuideButton() {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const prodUrl = "https://hauler-hub-23.emergent.host";

    const smsText =
        "WasteHQ driver app setup:\n" +
        "1. Open Safari (iPhone) or Chrome (Android)\n" +
        `2. Go to ${prodUrl}\n` +
        "3. Log in with the email + password we sent you\n" +
        "4. iPhone: tap Share -> Add to Home Screen\n" +
        "   Android: tap the 3-dot menu -> Install app\n" +
        "5. Open the icon on your home screen and Allow Location + Notifications";

    const copy = () => {
        navigator.clipboard.writeText(smsText);
        setCopied(true);
        toast.success("Copied — paste into a text to your driver");
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="driver-install-guide-btn">
                    <Smartphone className="w-4 h-4 mr-1" />Driver install guide
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="driver-install-guide-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Driver app — install instructions</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 text-sm">
                    <p className="text-muted-foreground">
                        No app store, no downloads. Drivers install directly from Safari (iPhone)
                        or Chrome (Android). 2 minutes each.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="border border-border rounded-md p-4 space-y-2">
                            <div className="font-heading font-semibold">iPhone</div>
                            <ol className="list-decimal list-inside space-y-1 text-sm">
                                <li>Open <strong>Safari</strong></li>
                                <li>Go to <code className="text-xs bg-secondary px-1 rounded">{prodUrl}</code></li>
                                <li>Log in</li>
                                <li>Tap <strong>Share</strong> (square with up-arrow)</li>
                                <li>Tap <strong>Add to Home Screen</strong> → <strong>Add</strong></li>
                            </ol>
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 pt-1">
                                Must be Safari on iPhone — Chrome won't install to home screen.
                            </p>
                        </div>

                        <div className="border border-border rounded-md p-4 space-y-2">
                            <div className="font-heading font-semibold">Android</div>
                            <ol className="list-decimal list-inside space-y-1 text-sm">
                                <li>Open <strong>Chrome</strong></li>
                                <li>Go to <code className="text-xs bg-secondary px-1 rounded">{prodUrl}</code></li>
                                <li>Log in</li>
                                <li>Tap <strong>⋮ menu</strong> (top-right)</li>
                                <li>Tap <strong>Install app</strong> → <strong>Install</strong></li>
                            </ol>
                        </div>
                    </div>

                    <div className="border border-border rounded-md p-4 space-y-2">
                        <div className="font-heading font-semibold">First-time setup (once per driver)</div>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Tap the WasteHQ icon on the home screen</li>
                            <li>Log in with the driver's email + password</li>
                            <li>Tap <strong>Allow</strong> when asked for <strong>Location</strong> (powers dispatch tracking + 10-min customer SMS)</li>
                            <li>Tap <strong>Allow</strong> for <strong>Notifications</strong> (gets the 200 ft arrival ding)</li>
                        </ul>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-md bg-secondary/50 border border-border">
                        <div className="text-xs text-muted-foreground">
                            Ready-to-text version — copy and paste into a message to any driver.
                        </div>
                        <Button size="sm" variant="outline" onClick={copy} data-testid="copy-install-text-btn">
                            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                            {copied ? "Copied" : "Copy for SMS"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
